import styled from '@emotion/styled'
import { INTERSTITIAL, MOBILE_INTERSTITIAL, OUT_OF_PAGE, SKIN_SIZE } from '@pubstack/common/src/adFormat'
import { AdUnit, ContainerMaxSize, getAdUnitMediatypes } from '@pubstack/common/src/adunit'
import { DeviceType } from '@pubstack/common/src/device-type'
import { NativeTemplate } from '@pubstack/common/src/nativeTemplate'
import { FunctionComponent, useEffect } from 'react'
import { FormProvider, SubmitHandler, useForm, useWatch } from 'react-hook-form'
import { Colors } from '~/assets/style/colors'
import { Fonts } from '~/assets/style/fonts'
import { EditPageWrapper, PageWrapper } from '~/components/_styled/page'
import Button from '~/components/Button'
import { ContentCard } from '~/components/ContentCard'
import { Link } from '~/components/Link'
import { LoadingModal } from '~/components/LoadingModal'
import { useGlobalModal } from '~/components/layout/GlobalModal'
import { Selector } from '~/components/Selector'
import { Skeleton } from '~/components/Skeleton'
import { StepperIndicator } from '~/components/StepperIndicator'
import { useToast } from '~/components/Toast/useToasts'
import { useCustomStep } from '~/hooks/useCustomStep'
import { WithClassName } from '~/types/utils'
import { AdFormatDisplay, useAdFormat } from './AdFormatDisplay'
import { SummaryCard } from './components/SummaryCard'
import { PureAdmAdUnitEditConfirmationModal } from './PureAdmAdUnitEditConfirmationModal'
import { PureAdmAdUnitFirstStep } from './PureAdmAdUnitFirstStep'
import { PureAdmAdUnitSecondStep } from './PureAdmAdUnitSecondStep'

export type AdUnitForm = AdUnit & {
  bannerEnabled: boolean
}

/**
 * Returns an object listing every key-value which is different in b from a
 */
const getAdUnitDiff = (a: AdUnitForm, b: AdUnitForm): Partial<AdUnit> => {
  return Object.fromEntries(Object.entries(b).filter(([key, value]) => JSON.stringify(a[key as keyof AdUnitForm]) !== JSON.stringify(value)))
}

const TitleBar = styled.div`
  display: flex;
  padding-bottom: 40px;
  width: 100%;
  justify-content: space-between;
  h1 {
    ${Fonts.H1}
  }
  ${Link} {
    margin-bottom: 12px;
  }
`

const AdmEditContentCard = styled(ContentCard)`
  width: 1000px;
`

const CardContent = styled.div`
  padding: 0 0 20px 0;
`

const CardActions = styled.div`
  display: flex;
  flex-direction: row;
  gap: 20px;
  justify-content: end;
`

const PreviousButton = styled(Button)`
  margin-right: auto;
`

const CardTitleWrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
`

const CardTitle = styled.h2`
  ${Fonts.colors.Jet}
  ${Fonts.H1}
  font-weight: 400;
  display: inline-flex;
  align-items: center;
  margin: 0;
  flex: 1;
`

export const ErrorMessage = styled.div`
  ${Fonts.P2}
  ${Fonts.colors.Alert}
  display: inline-flex;
  align-items: center;
  gap: 4px;
`
export const DeviceChoice = styled.div`
  display: inline-flex;
  gap: 10px;
  flex-wrap: wrap;

  & ${Selector} {
    width: 182px;
  }

  & ${ErrorMessage} {
    flex-basis: 100%;
  }
`
const BottomBar = styled.div`
  border-top: 1px solid ${Colors.Platinum};
  margin-top: 20px;
`

export const isSizeFitsContainer = (size: string, containerMaxSize: ContainerMaxSize | undefined): { both: boolean; width: boolean; height: boolean } => {
  const [width, height] = size.split('x').map((n) => Number.parseInt(n))
  const sizeFits = { width: (containerMaxSize?.width || Infinity) >= width, height: (containerMaxSize?.height || Infinity) >= height }
  return { ...sizeFits, both: sizeFits.width && sizeFits.height }
}

export const getPredefinedSizes = (adFormat: AdFormatDisplay, devices: DeviceType[]): string[] => {
  if (!adFormat.sizes.length || !devices.length) {
    return []
  }
  return adFormat.sizes.filter((sizesByDevice) => devices.some((device) => sizesByDevice.devices.includes(device))).map((sizeByDevice) => sizeByDevice.size)
}

type PureAdmAdUnitEditPageProps = WithClassName & {
  adUnit: AdUnit
  isLoading: boolean
  isEditing: boolean
  breadcrumbs: React.ReactNode
  onAdUnitsEditBackClick: () => void
  onCreateAdUnit: (AdUnit: AdUnit) => Promise<void>
  onUpdateAdUnit: (AdUnit: AdUnit) => Promise<void>
  validateUniqueName: (name?: string) => Promise<boolean>
  isSaving: boolean
  nativeTemplates: NativeTemplate[]
  hasInstreamFeature: boolean
  hasInterstitialFeature: boolean
}

const coalesceContainerMaxSize = (containerMaxSize: Partial<ContainerMaxSize> | undefined): ContainerMaxSize | undefined => {
  if (!containerMaxSize?.width && !containerMaxSize?.height) return undefined

  return {
    width: !containerMaxSize?.width ? undefined : containerMaxSize?.width,
    height: !containerMaxSize?.height ? undefined : containerMaxSize?.height,
  }
}

const UnstyledPureAdmAdUnitEditPage: FunctionComponent<PureAdmAdUnitEditPageProps> = ({
  adUnit,
  isLoading,
  isEditing,
  className,
  breadcrumbs,
  onAdUnitsEditBackClick,
  onCreateAdUnit,
  onUpdateAdUnit,
  validateUniqueName,
  isSaving,
  nativeTemplates,
  hasInstreamFeature,
  hasInterstitialFeature,
}) => {
  const modal = useGlobalModal()
  const formSteps = 2
  const [currentStep, stepHelpers] = useCustomStep(formSteps)
  const toast = useToast()

  const defaultValues: AdUnitForm = {
    ...adUnit,
    containerMaxSize: {
      // input expects a string, but the form control uses the end number type. undefined throws a react uncontrolled input error
      width: adUnit.containerMaxSize?.width || ('' as unknown as number),
      height: adUnit.containerMaxSize?.height || ('' as unknown as number),
    },
    bannerEnabled: true,
  }
  const formMethods = useForm<AdUnitForm>({ defaultValues, mode: 'onChange' })
  const {
    setValue,
    getValues,
    reset,
    trigger,
    handleSubmit,
    formState: { isDirty, isValid },
    control,
  } = formMethods

  // The summary reads this, not `getValues()`: `getValues` never changes identity, so React Compiler calls it
  // once and the card stays frozen on the ad unit the form started with. Typed `DeepPartial`, but every key is
  // seeded from `defaultValues`, so it holds a whole ad unit.
  const formValues = useWatch({ control }) as AdUnitForm
  const { sizes, customSizes, fluid, containerMaxSize, adFormat: storedAdFormat, devices, bannerEnabled } = formValues
  const adFormat = useAdFormat(storedAdFormat?.id)

  const onContinue = async () => {
    const valid = await trigger(['adFormat', 'adServerAdUnitName', 'divId', 'name', 'devices'])
    // An unresolved format used to block the step here too, with nothing on screen to say why. Only validity
    // decides now; the seeding below is what actually needs the format.
    if (!valid) {
      return
    }

    // We force some fields of the form's second step if it's an ad unit creation
    if (adFormat && !isEditing && adFormat.id !== adUnit.adFormat?.id) {
      setValue('sizes', adFormat.mediatypes?.includes('Banner') || adFormat.mediatypes?.includes('Instream') ? getPredefinedSizes(adFormat, devices) : [])
      // Header bidding cannot be disabled if we are editing and the HB is enabled on the original adunit
      setValue('headerBiddingEnabled', (isEditing && adUnit.headerBiddingEnabled) || (adFormat.id !== OUT_OF_PAGE.id && adFormat.id !== MOBILE_INTERSTITIAL.id && adFormat.id !== INTERSTITIAL.id))
    }

    stepHelpers.goToNextStep()
  }

  const checkMediatypes = (adUnit: AdUnitForm): boolean => {
    if (adUnit.adFormat && adUnit.headerBiddingEnabled && !adUnit.canOutstream && !adUnit.bannerEnabled && !adUnit.nativeTemplate && !adUnit.isInstream) {
      toast.alert('At least one mediatype must be selected')
      return false
    }
    return true
  }

  const onSubmit: SubmitHandler<AdUnitForm> = async (adUnit) => {
    if (!checkMediatypes(adUnit)) return
    modal.open(LoadingModal, {}, { showCloseButton: false, shouldCloseOnOverlayClick: false, shouldCloseOnEsc: false })
    const skinEnabled = !!sizes?.includes(SKIN_SIZE)
    const coalescedContainerMaxSize = coalesceContainerMaxSize(containerMaxSize)
    const coalescedAnchor = adUnit.anchor?.mode ? adUnit.anchor : undefined
    // As product wants to keep banner params to the end (in case the user reactivates it), we need to empty banner values only at the form submission
    isEditing
      ? await onUpdateAdUnit({ ...adUnit, containerMaxSize: coalescedContainerMaxSize, skinEnabled, anchor: coalescedAnchor })
      : await onCreateAdUnit({
          ...adUnit,
          containerMaxSize: coalescedContainerMaxSize,
          skinEnabled,
          anchor: coalescedAnchor,
          ...(!adUnit.bannerEnabled && !adUnit.isInstream ? { fluid: false, sizes: [], customSizes: [], skinEnabled: false } : []),
        })
    modal.close()
  }

  useEffect(() => {
    reset({ ...adUnit, bannerEnabled: isEditing ? getAdUnitMediatypes(adUnit).includes('Banner') : true })
  }, [adUnit])

  useEffect(() => {
    trigger(['fluid', 'sizes', 'customSizes'])
  }, [fluid, sizes, customSizes, bannerEnabled])

  return (
    <EditPageWrapper className={className}>
      <TitleBar>
        {breadcrumbs}
        <Button variant={'tertiary'} onClick={onAdUnitsEditBackClick} trackingLabel={'adm-forms-back'}>
          Back
        </Button>
      </TitleBar>
      <PageWrapper>
        <AdmEditContentCard color={Colors.Turquoise}>
          <CardTitleWrapper>
            <CardTitle>{isLoading ? <Skeleton bigger width={'120px'} /> : isEditing ? `Edit ${adUnit.name}` : 'Ad unit'}</CardTitle>
            <StepperIndicator steps={formSteps} color={Colors.Turquoise} currentStep={currentStep} />
          </CardTitleWrapper>
          <BottomBar />
          <FormProvider {...formMethods}>
            <form id={'newAdUnitForm'} onSubmit={handleSubmit(onSubmit)}>
              <CardContent>
                {currentStep === 1 && (
                  <PureAdmAdUnitFirstStep validateUniqueName={validateUniqueName} isEditing={isEditing} hasInstreamFeature={hasInstreamFeature} hasInterstitialFeature={hasInterstitialFeature} />
                )}
                {currentStep === 2 && <PureAdmAdUnitSecondStep adUnit={adUnit} isLoading={isLoading} isEditing={isEditing} nativeTemplates={nativeTemplates} />}
              </CardContent>
              <CardActions>
                {stepHelpers.canGoToPrevStep && (
                  <PreviousButton variant={'tertiary'} onClick={stepHelpers.goToPrevStep} trackingLabel={'adm-ad-units-previous'}>
                    Previous
                  </PreviousButton>
                )}
                <Button variant={'tertiary'} onClick={onAdUnitsEditBackClick} trackingLabel={'adm-ad-units-cancel'}>
                  Cancel
                </Button>
                {stepHelpers.canGoToNextStep ? (
                  <Button variant={'primary'} onClick={onContinue} disabled={isLoading} trackingLabel={'adm-ad-units-continue'}>
                    Continue
                  </Button>
                ) : isEditing ? (
                  <Button
                    trackingLabel={'adm-ad-units-save'}
                    disabled={!isDirty || !isValid || isSaving}
                    variant={'primary'}
                    data-ph-capture-attribute-adunit-format={adFormat?.name}
                    data-ph-capture-attribute-adunit-device={devices?.join('-')}
                    onClick={() => {
                      if (!checkMediatypes(getValues())) return
                      setValue('skinEnabled', sizes?.includes(SKIN_SIZE))
                      if (!bannerEnabled && !adFormat?.mediatypes?.includes('Instream')) {
                        setValue('fluid', false)
                        setValue('sizes', [])
                        setValue('customSizes', [])
                        setValue('skinEnabled', false)
                      }
                      modal.open(PureAdmAdUnitEditConfirmationModal, {
                        onValidate: async () => {
                          await modal.close()
                          handleSubmit(onSubmit)()
                        },
                        updateDiff: getAdUnitDiff({ ...adUnit, bannerEnabled: getAdUnitMediatypes(adUnit).includes('Banner') }, getValues()),
                      })
                    }}
                  >
                    Update
                  </Button>
                ) : (
                  <Button
                    trackingLabel={'adm-ad-units-save'}
                    data-ph-capture-attribute-adunit-format={adFormat?.name}
                    data-ph-capture-attribute-adunit-device={devices?.join('-')}
                    variant={'primary'}
                    disabled={isSaving || isLoading}
                    onClick={handleSubmit(onSubmit)}
                  >
                    Validate
                  </Button>
                )}
              </CardActions>
            </form>
          </FormProvider>
        </AdmEditContentCard>
        <div>
          <SummaryCard adUnit={formValues} />
        </div>
      </PageWrapper>
    </EditPageWrapper>
  )
}

export const PureAdmAdUnitEditPage = styled(UnstyledPureAdmAdUnitEditPage)``
